import { Point } from "@/routes/Frontend/FuturebilityCockpit/types";

export { UserIcon } from "./UserIcon";

export const QUADRANT_COUNT = 4;
export const DEFAULT_WIDTH = 350;
export const DEFAULT_HEIGHT = 350;

export interface BaseQuadrantProps {
  points: Point[];
  width?: number;
  height?: number;
  yTopTitle: string;
  yCenterTitle: string;
  yBottomTitle: string;
  xLeftTitle: string;
  xCenterTitle: string;
  xRightTitle: string;
  quadrantSidePanelTexts: string[];
  quadrantSidePanelTitles: string[];
}

export interface QuadrantAxisProps {
  points: Point[];
  width?: number;
  height?: number;
  xTitle: string;
  yTitle: string;
  bgColor?: string;
}

export const calculateAverage = (points: Point[]): { x: number; y: number } => {
  if (points.length === 0) return { x: 0, y: 0 };

  const sum = points.reduce(
    (acc, point) => ({
      x: acc.x + point.x,
      y: acc.y + point.y,
    }),
    { x: 0, y: 0 }
  );

  return {
    x: sum.x / points.length,
    y: sum.y / points.length,
  };
};

export const getXPosition = (x: number, width: number): number => x * width;

export const getYPosition = (y: number, height: number): number =>
  (1 - y) * height;

export const getLinesPosition = (
  x: number,
  y: number,
  width: number,
  height: number
) => ({
  x: getXPosition(x, width),
  y: getYPosition(y, height),
});

export interface QuadrantChartProps extends BaseQuadrantProps {
  quadrantTitles: string[];
}

export const getQuadrantIndex = (x: number, y: number): number => {
  // Quadrant mapping:
  // 0: top-right (x >= 0.5, y >= 0.5)
  // 1: top-left (x < 0.5, y >= 0.5)
  // 2: bottom-left (x < 0.5, y < 0.5)
  // 3: bottom-right (x >= 0.5, y < 0.5)

  if (x >= 0.5) {
    return y >= 0.5 ? 0 : 3; // right side: top or bottom
  } else {
    return y >= 0.5 ? 1 : 2; // left side: top or bottom
  }
};

export const getCountsByQuadrant = (points: Point[]): number[] => {
  const counts = new Array(QUADRANT_COUNT).fill(0);

  points.forEach(point => {
    const quadrantIndex = getQuadrantIndex(point.x, point.y);
    counts[quadrantIndex]++;
  });

  return counts;
};

export const calculatePercentageCounts = (counts: number[]): number[] => {
  const total = counts.reduce((sum, count) => sum + count, 0);
  if (total === 0) return counts.map(() => 0);

  return counts.map(count => Math.round((count / total) * 100 * 100) / 100);
};

export const getTopQuadrantIndex = (counts: number[]): number => {
  return counts.indexOf(Math.max(...counts));
};
